import { action } from 'mobx';
import STATUS, { stateCreator } from './states';
import TaskState from './TaskState';
import { createHandler } from './helpers';

/**
 * Creates a new task which wrapps the method
 *
 * @param {Function} fn - method beeing decorated
 * @returns {Function} Task
 */
export function createTask(fn) {
	/**
	 * Used to update state only once in case
	 * of multiple concurrent calls to the task
	 */
	const callCount = 0;

	/**
	 * Task function wrapper
	 * we need to wrap this in an mobx action
	 * so it updates the state in that class
	 */
	const task = action(async function taskFn(...args) {
		const callCountAtStart = callCount;

		try {
			task.setState(stateCreator(STATUS.loading));
			const result = await fn.call(this, ...args);

			/* istanbul ignore else  */
			if (callCountAtStart === callCount) {
				task.setState(stateCreator(STATUS.resolved, result));
			}
		} catch (error) {
			/* istanbul ignore else  */
			if (callCountAtStart === callCount) {
				task.setState(stateCreator(STATUS.rejected, error));
			}
			/**
			 * In development throw the error so we see in the console
			 * it helps in cases of syntax error that are caught here
			 */
			if (__DEV__) {
				throw error;
			}
		}
	});

	/**
	 * Enhance the method with props to be Used
	 * in components for task state handling
	 */
	Object.assign(task, {
		state: new TaskState(),
		handle: createHandler(task),
		setState: action(state => Object.assign(task.state, state)),
	});

	return task;
}

/**
 * Decorator meant to wrap a state method for easier
 * handling of async call
 *
 * This decorator wrap the method and enhances it with a state.
 * The method must return a promise so we can set the stated based
 * on that promise.
 *
 * It adds the following props to the method:
 *
 * state - The current state of the execution
 * 		status - One of loading, pending, resolved or rejected
 * 		error - Set to the error if rejected
 * 		result - Set to the result if resolved
 *
 * pending - the initial status of the state
 * loading - true when task is executing
 * resolved - true when task executed succesfully
 * rejected - true when task executed with error
 *
 * handle() - easy handling of state status see ./helpers (createHandler)
 *
 * @example
 * 	// state class
 * 	class MyState {
 * 		@task search(query = '') {
 * 			return http.get('something')
 * 		}
 * 	}
 *
 * 	// Component
 * 	render() {
 * 		return (
 * 			this.MyState.search.handle({
 * 				loading: () => <Loader />,
 * 				resolved: result => <div>{result}</div>,
 * 				rejected: error => <div>{error}</div>
 * 			})
 * 		)
 * 	}
 *
 * @param {Object} target Class containing the method being decorated
 * @param {string} name Name of the method beeing decorated
 * @param {Object} descriptor Descriptor object of the method
 *
 * @return {Object} Descriptor with value wrapped in a task
 */
export default function task(target, name, descriptor) {
	//TODO: Make sure descriptor.value is a function
	descriptor.value = createTask(descriptor.value);
	return descriptor;
}



// WEBPACK FOOTER //
// ./src/state/helpers/task/index.js